import React, { useState } from 'react';
import {
  GearIcon, ChevronUpIcon, ChevronDownIcon, PlusIcon,
} from '@revfluence/fresh-icons/regular/esm';
// import { Badge } from '@frontend/shadcn/components/ui/badge';
// import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@frontend/shadcn/components/ui/select';
// import { useCustomStages } from '../context/CustomStagesContext';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { cn } from '@frontend/shadcn/lib/utils';

interface TaskSettingsSectionProps {
  creatorAccess: boolean;
  // ctaLabel?: string;
  // onCtaLabelChange?: (label: string) => void;
  statuses: Array<{ id: number; name: string; priority: number }>;
  selectedTaskStatuses: Array<{ id: number; name: string }>;
  selectedResultingStatus: { id: number; name: string } | null;
  onTaskSettingsChange: (settings: {
    initialStatuses: Array<{ id: number; name: string }>;
    finalStatus: { id: number; name: string } | null;
  }) => void;
}

export const TaskSettingsSection: React.FC<TaskSettingsSectionProps> = ({
  creatorAccess,
  // ctaLabel = 'Submit',
  // onCtaLabelChange,
  statuses = [],
  selectedTaskStatuses = [],
  selectedResultingStatus,
  onTaskSettingsChange
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showResultingStatusDropdown, setShowResultingStatusDropdown] = useState(false);

  const addTaskStatus = (status: { id: number; name: string; priority: number }) => {
    if (!selectedTaskStatuses.find((s) => s.id === status.id)) {
      onTaskSettingsChange({
        initialStatuses: [...selectedTaskStatuses, { id: status.id, name: status.name }],
        finalStatus: selectedResultingStatus
      });
    }
    setShowStatusDropdown(false);
  };

  const removeTaskStatus = (statusId: number) => {
    onTaskSettingsChange({
      initialStatuses: selectedTaskStatuses.filter((s) => s.id !== statusId),
      finalStatus: selectedResultingStatus
    });
  };

  const availableStatuses = statuses.filter(
    (status) => !selectedTaskStatuses.find((selected) => selected.id === status.id)
  );

  const selectResultingStatus = (status: { id: number; name: string }) => {
    onTaskSettingsChange({
      initialStatuses: selectedTaskStatuses,
      finalStatus: status
    });
    setShowResultingStatusDropdown(false);
  };

  return (
    <div className="self-stretch p-6 bg-white rounded-2xl outline outline-1 outline-offset-[-1px] outline-gray-200 inline-flex flex-col justify-start items-start gap-4">
      <div className="self-stretch flex flex-col justify-center items-start gap-1">
        <div className="self-stretch inline-flex justify-start items-center gap-2">
          <div className="w-6 h-6 p-1 bg-orange-500 rounded-lg flex items-center justify-center">
            <GearIcon className="w-3 h-3 text-white" />
          </div>
          <div className="flex-1 flex justify-start items-center gap-2">
            <div className="w-64 flex justify-start items-center gap-2">
              <div className="justify-start text-gray-800 text-base font-medium font-['Inter'] leading-7">Task Settings</div>
            </div>
          </div>
          <button
            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <ChevronUpIcon className={cn('w-3.5 h-3.5 text-gray-800 transition-transform', !isExpanded && 'rotate-180')} />
          </button>
        </div>
        <div className="self-stretch justify-start text-gray-500 text-sm font-normal font-['Inter'] leading-normal">
          Set when the task becomes available and choose the resulting status for creators after completing it.
        </div>
      </div>

      {isExpanded && (
        <>
          {!creatorAccess ? (
            <div className="self-stretch p-2 bg-white flex justify-center items-start">
              <p className="text-gray-500 text-sm text-center">
                Enable Creator Access to configure task settings.
              </p>
            </div>
          ) : (
            <>
              <div className="self-stretch inline-flex justify-start items-start gap-6">
                <div className="flex-1 inline-flex flex-col justify-start items-start">
                  <div className="self-stretch pb-2 inline-flex justify-start items-center gap-1">
                    <div className="justify-start text-zinc-900 text-xs font-medium font-['Inter'] leading-tight">Enable Task in Status</div>
                    <InformationCircleIcon className="w-3 h-3 text-gray-300" />
                  </div>
                  <div className="self-stretch min-h-[32px] px-3 py-2 bg-white rounded-lg outline outline-1 outline-offset-[-1px] outline-gray-200 inline-flex flex-wrap items-center gap-1.5 relative">
                    {selectedTaskStatuses.map((status) => (
                      <div key={status.id} className="px-2 py-0.5 bg-gray-200 rounded-lg inline-flex justify-start items-center gap-1">
                        <div className="text-gray-800 text-xs font-normal font-['Inter'] leading-tight">{status.name}</div>
                        <button
                          onClick={() => removeTaskStatus(status.id)}
                          className="w-3 h-3 flex justify-center items-center cursor-pointer hover:bg-gray-300 rounded"
                        >
                          <div className="text-gray-800 text-xs font-black leading-none">×</div>
                        </button>
                      </div>
                    ))}

                    {availableStatuses.length > 0 && (
                      <div className="relative inline-block">
                        <button
                          onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                          className="px-2 py-0.5 text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
                        >
                          <PlusIcon className="w-3 h-3" />
                          Add Status
                        </button>

                        {showStatusDropdown && (
                          <div className="absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-32">
                            {availableStatuses.map((status) => (
                              <button
                                key={status.id}
                                onClick={() => addTaskStatus(status)}
                                className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg"
                              >
                                {status.name}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex-1 inline-flex flex-col justify-start items-start">
                  <div className="self-stretch pb-2 inline-flex justify-start items-center gap-1">
                    <div className="justify-start text-zinc-900 text-xs font-medium font-['Inter'] leading-tight">Resulting Status</div>
                    <InformationCircleIcon className="w-3 h-3 text-gray-300" />
                  </div>
                  <div className="self-stretch h-8 px-3 py-1 bg-white rounded-lg outline outline-1 outline-offset-[-1px] outline-gray-200 inline-flex justify-between items-center relative">
                    <div className="flex-1 text-gray-800 text-sm font-normal font-['Inter'] leading-normal">
                      {selectedResultingStatus ? selectedResultingStatus.name : 'Select status'}
                    </div>
                    <button
                      onClick={() => setShowResultingStatusDropdown(!showResultingStatusDropdown)}
                      className="flex items-center justify-center gap-2.5"
                    >
                      <div className="w-3 h-3 relative flex items-center justify-center">
                        <ChevronDownIcon className={cn('w-3 h-3 text-gray-800 transition-transform', showResultingStatusDropdown && 'rotate-180')} />
                      </div>
                    </button>

                    {showResultingStatusDropdown && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-40 overflow-y-auto">
                        {statuses.map((status) => (
                          <button
                            key={status.id}
                            onClick={() => selectResultingStatus(status)}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg"
                          >
                            {status.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* <div className="w-80 inline-flex justify-start items-start gap-3">
                <div className="flex-1 inline-flex flex-col justify-start items-start">
                  <div className="pb-2 inline-flex justify-start items-center gap-1">
                    <div className="justify-start text-gray-800 text-xs font-medium font-['Inter'] leading-tight">Button Label</div>
                  </div>
                  <div className="self-stretch px-3 py-1 bg-white rounded-lg outline outline-1 outline-offset-[-1px] outline-gray-200 inline-flex justify-start items-center gap-2.5">
                    <input
                      type="text"
                      value={ctaLabel}
                      onChange={(e) => onCtaLabelChange?.(e.target.value)}
                      className="flex-1 justify-start text-gray-800 text-sm font-normal font-['Inter'] leading-normal bg-transparent border-none outline-none"
                      placeholder="Submit"
                    />
                  </div>
                </div>
              </div> */}
            </>
          )}
        </>
      )}
    </div>
  );
};
